import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useReducer } from 'react';
import { useDtoolsAuthenticate } from 'src/graphql/entities/integrations/dtools/hooks/use-dtools-authenticate';
import { useNotifications } from 'src/utils/hooks';

type Action =
  | { type: 'API_KEY_CHANGE'; payload: string }
  | { type: 'RESET' }
  | { type: 'COMPLETE' }
  | { type: 'ADD_API_KEY' };

type State = {
  apiKey: string;
  isConnected: boolean;
  isEditMode: boolean;
};

export const DtoolsConnectIntegration = () => {
  const { showErrorAlert } = useNotifications();
  const theme = useTheme();
  const { connect } = useDtoolsAuthenticate();

  const [state, dispatch] = useReducer(
    (state: State, action: Action) => {
      switch (action.type) {
        case 'API_KEY_CHANGE':
          return {
            ...state,
            apiKey: action.payload,
          };
        case 'COMPLETE': {
          return {
            ...state,
            isEditMode: false,
            isConnected: true,
          };
        }
        case 'ADD_API_KEY':
          return { ...state, isEditMode: true, apiKey: '' };
        case 'RESET':
          return { ...state, isEditMode: false, isConnected: false, apiKey: '' };
        default:
          return state;
      }
    },
    { apiKey: '', isConnected: false, isEditMode: false },
  );
  const isValid = !!state.apiKey;

  const onConnectHandler = useCallback(async () => {
    if (!isValid) {
      return;
    }
    await connect({
      variables: {
        params: {
          apiKey: state.apiKey,
        },
      },
      onError: (error) => {
        showErrorAlert(error.message);
      },
      onCompleted: () => {
        dispatch({ type: 'COMPLETE' });
      },
    });
  }, [connect, isValid, showErrorAlert, state.apiKey]);

  const onCancelHandler = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  if (state.isEditMode) {
    return (
      <Card variant="outlined">
        <CardHeader
          avatar={<Avatar alt="d-tools-logo" src="/d-tools-logo.png" />}
          title="Connect D-Tools Cloud"
        />
        <CardContent
          sx={{
            paddingTop: 0,
          }}
        >
          <TextField
            required
            label="API Key"
            placeholder="Enter your API key"
            type="password"
            autoComplete="off"
            size="small"
            onChange={(e) => dispatch({ type: 'API_KEY_CHANGE', payload: e.target.value })}
            sx={{
              width: '100%',
              marginTop: theme.spacing(),
              marginBottom: theme.spacing(2),
            }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Your API key will be encrypted and stored securely.
          </Typography>
        </CardContent>
        <CardActions sx={{ m: theme.spacing(), justifyContent: 'end' }}>
          <Button size="small" variant="outlined" onClick={onCancelHandler}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onConnectHandler}
            disabled={!isValid}
          >
            Connect
          </Button>
        </CardActions>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<Avatar alt="d-tools-logo" src="/d-tools-logo.png" />}
        title="D-Tools Cloud"
        subheader="Not connected"
      />
      <CardContent>
        <Typography gutterBottom variant="body2">
          Import proposals from D-Tools Cloud into site files to generate rich site documentation
        </Typography>
      </CardContent>
      <CardActions sx={{ m: theme.spacing(), justifyContent: 'end' }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => dispatch({ type: 'ADD_API_KEY' })}
          color="primary"
          fullWidth
        >
          Connect
        </Button>
      </CardActions>
    </Card>
  );
};
